import { PostFormData } from '@buildwise/pages/addPost/hooks/useAddPostForm';
import { styled } from '@mui/material/styles';
import { Box, FormControl, FormHelperText, Typography } from '@mui/material';
import Editor, { initConfig } from 'components/common/editor/Editor';
import TinyMceEditorSkelton from 'components/utils/TinyMceEditorSkelton';
import { ConfigEntryType } from 'config/config';
import { useCallback, useEffect, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { ArticleSourceType } from '@buildwise/pages/knowledge/hooks/useArticlesQuery';

type EditorFieldProps = {
  field: ConfigEntryType;
  autoFocus?: boolean;
  defaultHeight?: number;
  hideToolbar?: boolean;
  isEditing?: boolean;
  articleType?: ArticleSourceType;
};

function EditorField({
  field,
  isEditing,
  autoFocus,
  defaultHeight,
  hideToolbar,
}: EditorFieldProps) {
  const {
    control,
    formState: { isSubmitting, errors },
    setValue,
    setFocus,
    watch,
  } = useFormContext<PostFormData>();
  const description = watch(`${field.entryCode}`);
  const [isLoadingEditor, setLoadingEditor] = useState(true);
  const [showEditor, setShowEditor] = useState(isEditing || !!description || autoFocus);
  const onShowEditor = useCallback(() => {
    setShowEditor(true);
    setFocus(field.entryCode);
  }, []);

  useEffect(() => {
    if (!showEditor && description) {
      onShowEditor();
    }
  }, [description]);
  return (
    <Box position="relative" mb={1}>
      {!showEditor && (
        <>
          <PlaceholderContainer
            tabIndex={0}
            height={defaultHeight || 250}
            onClick={onShowEditor}
            onFocus={onShowEditor}
          >
            <DescriptionPlaceholder>
              {field?.referenceObject?.placeHolder || 'Add a description'}
            </DescriptionPlaceholder>
          </PlaceholderContainer>
          <FormHelperText error sx={{ mt: -1.5 }}>
            {(errors[field.entryCode]?.message as string) || ''}
          </FormHelperText>
        </>
      )}
      <Box sx={{ position: 'relative' }}>
        <Box
          sx={{
            visibility: showEditor ? 'visible' : 'hidden',
            minHeight: defaultHeight || 250,
            position: showEditor ? 'relative' : 'absolute',
          }}
        >
          <Controller
            control={control}
            render={({ field: { ref, onChange, ...controllerField }, fieldState }) => (
              <FormControl fullWidth>
                <Editor
                  {...controllerField}
                  id={field.entryCode}
                  onEditorChange={onChange}
                  onInit={(_, editor) => {
                    ref(editor);
                    // editor.editorContainer.style.transition = 'height 0.3s';
                    setLoadingEditor(false);
                  }}
                  value={!!controllerField?.value?.trim() ? controllerField.value : ''}
                  disabled={isSubmitting}
                  init={{
                    placeholder: field?.referenceObject?.placeHolder || 'Add a description',
                    min_height: defaultHeight || 250,
                    height: defaultHeight || 250,
                    auto_focus: autoFocus ? true : undefined,
                    ...(hideToolbar
                      ? { toolbar: false }
                      : {
                          toolbar: `${initConfig()?.toolbar ?? ''} emoticons`,
                        }),
                    plugins: [...(initConfig()?.plugins || []), 'autoresize', 'emoticons'],
                  }}
                  onBlur={() => {
                    controllerField.onBlur();
                    setValue(field.entryCode, controllerField?.value?.trim() || '');
                  }}
                />
                <FormHelperText error>{fieldState.error?.message}</FormHelperText>
              </FormControl>
            )}
            name={`${field.entryCode}`}
          />
          {isLoadingEditor && <TinyMceEditorSkelton />}
        </Box>
      </Box>
    </Box>
  );
}

const PlaceholderContainer = styled(Typography)(({ theme }) => ({
  cursor: 'text',
  padding: '6px 8px',
  transition: 'background-color 0.3s',
  backgroundColor: theme.palette.custom.whiteSmoke,
  borderRadius: '4px',
  marginBottom: 16,
  display: 'flex',
  alignItems: 'flex-start',
  '&:hover': {
    backgroundColor: theme.palette.custom.inputHoverBackground,
  },
}));

const DescriptionPlaceholder = styled(Typography)(({ theme }) => ({
  fontSize: 16,
  fontWeight: 600,
  color: theme.palette.custom.coolGrey,
}));

export default EditorField;
